import React, { useState, useEffect } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  ButtonGroup,
  Button,
  Spinner,
} from "reactstrap"
import ReactApexChart from "react-apexcharts"
import { useSelector } from "react-redux"
import axios from "axios"
import config from "config/config"

const ReachChart = ({ syncKey }) => {
  const activeOrganization = useSelector(
    state => state.organizationReducer.activeOrganization,
  )
  const userData = useSelector(state => state.Login.userData)
  const mode = useSelector(state => state.Layout.layoutMode)
  const { organizations, isConsolidatedView } = useSelector(
    state => state.organizationReducer,
  )

  const [dataGranularity, setDataGranularity] = useState("monthly") // "daily" or "monthly"
  const [isLoading, setIsLoading] = useState(false)
  const [chartData, setChartData] = useState({
    options: {
      colors: ["#2990DF"],
      chart: {
        toolbar: {
          show: false,
        },
      },
      xaxis: {
        categories: [], // Days or Months
        labels: {
          formatter: val => val,
        },
      },
      yaxis: {
        title: {
          text: "Reach",
        },
      },
    },
    series: [
      {
        name: "Reach",
        data: [], // Reach data
      },
    ],
  })

  const fetchInstagramInsights = async (force = false) => {
    try {
      setIsLoading(true)

      const organizationIds = isConsolidatedView
        ? organizations.map(org => org.Organization.id)
        : [activeOrganization.id]

      const resp = await axios.get(
        `${config.backendUrl}/instagram/dashboard/reach-chart`,
        {
          params: {
            organizationIds: organizationIds.join(","),
            granularity: dataGranularity,
            force,
          },
          headers: { Authorization: `Bearer ${userData.token}` },
          skipLoader: true,
        },
      )

      setChartData(prevData => ({
        ...prevData,
        options: {
          ...prevData.options,
          xaxis: {
            ...prevData.options.xaxis,
            categories: resp.data.categories,
          },
        },
        series: resp.data.series,
      }))
    } catch (err) {
      console.error(
        "Error fetching Instagram insights:",
        err.response?.data || err.message,
      )
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchInstagramInsights()
  }, [activeOrganization, dataGranularity, isConsolidatedView, organizations]) // Re-fetch when granularity or view mode changes

  useEffect(() => {
    if (syncKey) fetchInstagramInsights(true)
  }, [syncKey])

  return (
    <React.Fragment>
      <Card style={{ minHeight: "350px" }} className="br">
        {isLoading ? (
          <div
            style={{
              minHeight: "350px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Spinner
              size="lg"
              style={{
                color: mode === "dark" ? "#fff" : "#2990DF",
              }}
            />
          </div>
        ) : (
          <CardBody style={{ minHeight: "50vh" }}>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h4 className="card-title">Instagram Reach</h4>
              <ButtonGroup style={{}}>
                {/* Monthly Button */}
                <Button
                  size="sm"
                  onClick={() => setDataGranularity("monthly")}
                  style={{
                    minWidth: "150px",
                    height: "36px",
                    padding: "4px 10px",
                    fontWeight: 500,
                    fontSize: "13px",
                    lineHeight: 1.2,
                    transition: "all 0.2s ease",
                    backgroundColor:
                      dataGranularity === "monthly" ? "#2990DF" : "transparent",
                    color:
                      dataGranularity === "monthly"
                        ? "#fff"
                        : mode === "dark"
                          ? "#d1d5db"
                          : "#374151",
                    borderColor:
                      dataGranularity === "monthly"
                        ? "#2990DF"
                        : mode === "dark"
                          ? "#444"
                          : "#ccc",
                  }}
                >
                  Monthly (1 Year)
                </Button>

                {/* Daily Button */}
                <Button
                  size="sm"
                  onClick={() => setDataGranularity("daily")}
                  style={{
                    minWidth: "150px",
                    height: "36px",
                    padding: "4px 10px",
                    fontWeight: 500,
                    fontSize: "13px",
                    lineHeight: 1.2,
                    transition: "all 0.2s ease",
                    backgroundColor:
                      dataGranularity === "daily" ? "#2990DF" : "transparent",
                    color:
                      dataGranularity === "daily"
                        ? "#fff"
                        : mode === "dark"
                          ? "#d1d5db"
                          : "#374151",
                    borderColor:
                      dataGranularity === "daily"
                        ? "#2990DF"
                        : mode === "dark"
                          ? "#444"
                          : "#ccc",
                  }}
                >
                  Daily (2 Weeks)
                </Button>
              </ButtonGroup>
            </div>

            <Row>
              {/* Chart */}
              <div>
                <ReactApexChart
                  options={chartData.options}
                  series={chartData.series}
                  type="line" // Use "bar" for a bar chart
                  height="290"
                />
              </div>
            </Row>
          </CardBody>
        )}
      </Card>
    </React.Fragment>
  )
}

export default ReachChart